
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Search,
  Filter,
  RotateCcw,
  Download,
  Building2,
  CreditCard,
} from "lucide-react";
import * as XLSX from "xlsx";
import "./admin.css";

import {
  getAllPartners,
  getCompanyDetailsByPartnerId,
} from "../services/PartnerService";

const normalizeStatus = (value) =>
  String(value || "").trim().toUpperCase();

const getResponseBody = (response) =>
  response?.data?.data ?? response?.data;

function PartnerList() {
  const navigate = useNavigate();

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [city, setCity] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const recordsPerPage = 6;

  const fetchPartners = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getAllPartners();
      const body = getResponseBody(response);

      const rows = Array.isArray(body)
        ? body
        : Array.isArray(body?.content)
          ? body.content
          : [];

      // A partner must be approved before checking company verification.
      const approvedPartners = rows.filter(
        (partner) =>
          normalizeStatus(partner.status) === "APPROVED"
      );

      // Ignore missing company-details records and API failures individually.
      const verifiedResults = await Promise.allSettled(
        approvedPartners.map(async (partner) => {
          const companyResponse =
            await getCompanyDetailsByPartnerId(partner.id);

          const company = getResponseBody(companyResponse);

          if (
            !company ||
            Array.isArray(company) ||
            typeof company !== "object" ||
            normalizeStatus(company.verificationStatus) !== "VERIFIED"
          ) {
            return null;
          }

          return {
            ...partner,
            companyDetails: company,
            verificationStatus: "VERIFIED",
          };
        })
      );

      const verifiedPartners = verifiedResults
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value)
        .filter(Boolean);

      setPartners(verifiedPartners);
    } catch (error) {
      console.error("PARTNER LIST FETCH ERROR:", error);
      alert(
        error.response?.data?.message ||
          "Unable to load verified partners."
      );
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const filteredPartners = useMemo(() => {
    let data = [...partners];

    if (search.trim()) {
      const query = search.trim().toLowerCase();

      data = data.filter((partner) =>
        [
          partner.companyName,
          partner.name,
          partner.email,
          partner.mobile,
          partner.gst,
          partner.pan,
          partner.city,
          partner.companyDetails?.companyLegalName,
          partner.companyDetails?.gst,
        ].some((value) =>
          String(value || "").toLowerCase().includes(query)
        )
      );
    }

    if (companyType) {
      data = data.filter(
        (partner) =>
          String(
            partner.companyType ||
              partner.companyDetails?.companyType ||
              ""
          ) === companyType
      );
    }

    if (city.trim()) {
      const query = city.trim().toLowerCase();

      data = data.filter((partner) =>
        String(
          partner.city || partner.companyDetails?.city || ""
        ).toLowerCase().includes(query)
      );
    }

    return data;
  }, [partners, search, companyType, city]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, companyType, city]);

  const totalPages = Math.ceil(
    filteredPartners.length / recordsPerPage
  );

  const currentPartners = filteredPartners.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  const clearFilter = () => {
    setSearch("");
    setCompanyType("");
    setCity("");
    setCurrentPage(1);
  };

  const handleExport = () => {
    if (!filteredPartners.length) {
      alert("No verified partner data available.");
      return;
    }

    const exportData = filteredPartners.map((partner, index) => {
      const company = partner.companyDetails || {};

      return {
        "S.No": index + 1,
        "Partner ID": partner.id,
        "Company Name":
          company.companyLegalName ||
          company.companyName ||
          partner.companyName ||
          "",
        "Company Type":
          company.companyType || partner.companyType || "",
        "Contact Person": partner.name || "",
        Designation: partner.designation || "",
        Email: partner.email || "",
        Mobile: partner.mobile || "",
        GST: company.gst || partner.gst || "",
        PAN: company.pan || partner.pan || "",
        Address: company.address || partner.address || "",
        City: company.city || partner.city || "",
        State: company.state || partner.state || "",
        Pincode: company.pincode || partner.pincode || "",
        Website: company.website || partner.website || "",
        "Partner Status": partner.status || "",
        "Company Verification": partner.verificationStatus,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Verified Partners"
    );

    XLSX.writeFile(workbook, "Verified_Partner_List.xlsx");
  };

  return (
    <div className="partner-page">
      <div className="partner-header">
        <div className="partner-header-left">
          <div className="partner-title-icon">
            <ShieldCheck size={20} />
          </div>

          <div>
            <h1>All Verified Partners</h1>
            <p>
              Manage approved partners with verified company details
            </p>
          </div>
        </div>

        <div className="partner-header-actions">
          <button
            type="button"
            className="partner-export-btn"
            onClick={handleExport}
          >
            <Download size={15} /> Export Excel
          </button>
        </div>
      </div>

      <div className="partner-filter-card">
        <div className="partner-filter-title">
          <div className="partner-filter-icon">
            <Filter size={16} />
          </div>

          <div>
            <h3>Partner Filters</h3>
            <span>Search and filter verified partners</span>
          </div>
        </div>

        <div className="partner-filter-grid">
          <div className="partner-search">
            <Search size={15} />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search Company / Name / Email / Mobile"
            />
          </div>

          <select
            value={companyType}
            onChange={(event) =>
              setCompanyType(event.target.value)
            }
          >
            <option value="">All Company Types</option>
            <option value="Training Partner">Training Partner</option>
            <option value="Training Company">Training Company</option>
            <option value="Corporate">Corporate</option>
            <option value="Institute">Institute</option>
            <option value="Consultancy">Consultancy</option>
            <option value="Other">Other</option>
          </select>

          <input
            className="partner-simple-input"
            value={city}
            onChange={(event) => setCity(event.target.value)}
            placeholder="Filter by City"
          />

          <button
            type="button"
            className="partner-clear-btn"
            onClick={clearFilter}
          >
            <RotateCcw size={14} /> Clear
          </button>
        </div>
      </div>

      <div className="partner-table-card">
        <div className="partner-table-header">
          <div>
            <h2>Verified Partner List</h2>
            <span>
              Showing {filteredPartners.length} verified partners
            </span>
          </div>

          {totalPages > 0 && (
            <div className="trainer-pagination">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() =>
                  setCurrentPage((page) => page - 1)
                }
              >
                Prev
              </button>

              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  type="button"
                  key={index}
                  className={
                    currentPage === index + 1 ? "active" : ""
                  }
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </button>
              ))}

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((page) => page + 1)
                }
              >
                Next
              </button>
            </div>
          )}
        </div>

        <div className="partner-table-wrapper">
          {loading ? (
            <div className="partner-empty">
              Loading verified partners...
            </div>
          ) : currentPartners.length === 0 ? (
            <div className="partner-empty">
              No verified partners found. Verify the company details
              in the previous workflow first.
            </div>
          ) : (
            <table className="employee-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Company</th>
                  <th>Contact Person</th>
                  <th>Address</th>
                  <th>Company Details</th>
                  <th>Subscription Details</th>
                </tr>
              </thead>

              <tbody>
                {currentPartners.map((partner, index) => {
                  const company = partner.companyDetails || {};

                  return (
                    <tr key={partner.id}>
                      <td>
                        {(currentPage - 1) * recordsPerPage +
                          index +
                          1}
                      </td>

                      <td>
                        <div className="partner-company-details">
                          <strong className="partner-company-name">
                            {company.companyLegalName ||
                              company.companyName ||
                              partner.companyName ||
                              "-"}
                          </strong>

                          <span>
                            Type:{" "}
                            {company.companyType ||
                              partner.companyType ||
                              "-"}
                          </span>

                          <span>
                            GST: {company.gst || partner.gst || "-"}
                          </span>

                          <span>
                            PAN: {company.pan || partner.pan || "-"}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="partner-contact-details">
                          <strong>{partner.name || "-"}</strong>
                          <span>{partner.designation || "-"}</span>
                          <span>{partner.email || "-"}</span>
                          <span>{partner.mobile || "-"}</span>
                        </div>
                      </td>

                      <td>
                        <div className="partner-address-details">
                          <span>
                            {company.address || partner.address || "-"}
                          </span>

                          <span>
                            {company.city || partner.city || ""}
                            {(company.city || partner.city) &&
                            (company.state || partner.state)
                              ? ", "
                              : ""}
                            {company.state || partner.state || ""}
                          </span>

                          <small>
                            Pincode:{" "}
                            {company.pincode || partner.pincode || "-"}
                          </small>
                        </div>
                      </td>

                      <td>
                        <button
                          type="button"
                          className="partner-detail-btn"
                          onClick={() =>
                            navigate(
                              `/admin/company-details/${partner.id}`
                            )
                          }
                        >
                          <Building2 size={15} />
                          Company Details
                        </button>
                      </td>

                      <td>
                        <button
                          type="button"
                          className="partner-detail-btn subscription"
                          onClick={() =>
                            navigate(
                              `/admin/subscription-details/${partner.id}`
                            )
                          }
                        >
                          <CreditCard size={15} />
                          Subscription Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default PartnerList;
